import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useDeleteProject,
  useProjectList,
  useProjectsStore,
} from '@/features/project'

const DeleteProjectDialog: React.FC = () => {
  const { t } = useTranslation()
  const {
    deletingProject,
    deleteDialogOpen,
    setDeleteDialogOpen,
    clearDeletingProject,
    page,
    rowsPerPage,
    setPage,
    searchText,
    filters,
    sort,
  } = useProjectsStore()

  const { data } = useProjectList({
    search: searchText,
    page,
    rowsPerPage,
    sort,
    filters,
  })
  const currentPageItems = data?.data?.length || 0

  // Delete mutation
  const deleteMutation = useDeleteProject()

  const handleOpenChange = (newOpen: boolean) => {
    setDeleteDialogOpen(newOpen)
    if (!newOpen) {
      clearDeletingProject()
      deleteMutation.reset()
    }
  }

  const handleConfirm = () => {
    if (!deletingProject) return

    deleteMutation.mutate(deletingProject.id, {
      onSuccess: () => {
        triggerToast('success', t('project.dialog.messages.deleteSuccess'))
        setDeleteDialogOpen(false)
        clearDeletingProject()

        if (currentPageItems === 1 && page > 0) {
          setPage(page - 1)
        }
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message?: string }>
        const errorMessage =
          axiosError.response?.data?.message ||
          axiosError.message ||
          t('project.dialog.messages.errorGeneric')

        triggerToast('error', errorMessage)
      },
    })
  }

  const isDeleting = useMemo(
    () => deleteMutation.isPending,
    [deleteMutation.isPending]
  )

  return (
    <Dialog open={deleteDialogOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="sm:max-w-[480px] bg-white shadow-xl rounded-2xl p-6">
          <DialogHeader className="space-y-0.5">
            <DialogTitle className="m-0 font-semibold text-md text-primary-900">
              {t('project.dialog.deleteTitle')}
            </DialogTitle>
            <DialogDescription className="text-sm text-tertiary-600">
              {t('project.dialog.deleteDescription', {
                name: deletingProject?.project_name || '',
              })}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3">
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid bg-white"
              variant="default"
              onClick={() => handleOpenChange(false)}
              disabled={isDeleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid border-transparent !text-white !bg-error-solid"
              variant="default"
              onClick={handleConfirm}
              disabled={isDeleting}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default DeleteProjectDialog
